import { useEffect, useState } from 'react';

const HOST_ID = 'kundo-widget';
const TOGGLE_SELECTOR = '#kundo-widget-toggle';
const WINDOW_SELECTOR = '[data-testid="widget-window"]';

/**
 * Observes the Kundo chat widget's shadow DOM and returns:
 * - `host`: the widget shadow host element (light DOM)
 * - `button`: the toggle element inside the shadow root — `null` while the
 *   chat window is open (Kundo removes it from the DOM) and re-populated when
 *   the chat closes (so consumers can re-apply styles).
 * - `isChatOpen`: derived from the always-present chat window's inline
 *   `visibility` style. The widget persists open/closed state in localStorage,
 *   so the chat can start open on page load — relying on the toggle's absence
 *   to detect "open" gives a false positive during boot.
 */
export const useKundoChatToggle = () => {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [button, setButton] = useState<HTMLElement | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let shadowObserver: MutationObserver | null = null;

    const sync = (shadowRoot: ShadowRoot) => {
      const win = shadowRoot.querySelector<HTMLElement>(WINDOW_SELECTOR);
      const toggle = shadowRoot.querySelector<HTMLElement>(TOGGLE_SELECTOR);
      setIsChatOpen(win?.style.visibility === 'visible');
      setButton(prev => (prev === toggle ? prev : toggle));
    };

    const tryAttach = () => {
      if (shadowObserver) {
        return;
      }
      const el = document.getElementById(HOST_ID);
      if (!el?.shadowRoot) {
        return;
      }
      setHost(el);
      const shadowRoot = el.shadowRoot;
      shadowObserver = new MutationObserver(() => sync(shadowRoot));
      shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style']
      });
      sync(shadowRoot);
    };

    tryAttach();

    const bodyObserver = new MutationObserver(tryAttach);
    bodyObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      bodyObserver.disconnect();
      shadowObserver?.disconnect();
    };
  }, []);

  return {
    host,
    button,
    isChatOpen
  };
};
